import { ArrowUpTrayIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useRef, useState } from 'react';
import { useContentFileUrl } from '../hooks/useContentFiles';

interface PdfFieldProps {
    /** The newly picked file, if any. */
    file: File | null;
    /** Id of the stored PDF, shown as a removable row while no new file is picked. */
    storedFileId?: string | null;
    disabled?: boolean;
    onPick: (file: File | null) => void;
    /** Clears the stored PDF; validation still requires a replacement before save. */
    onRemoveStored?: () => void;
}

function formatBytes(bytes: number): string {
    const mb = bytes / 1024 / 1024;
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Drop-zone PDF picker; a stored PDF shows as its own row until removed. */
export const PdfField: React.FC<PdfFieldProps> = ({
    file,
    storedFileId = null,
    disabled,
    onPick,
    onRemoveStored
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    // The stored row only carries a fileId; the original upload name comes from
    // the resolve endpoint. Falls back to a generic label while loading.
    const { name: storedName } = useContentFileUrl(file ? null : storedFileId);

    const takeFirstPdf = (files: FileList | null) => {
        const dropped = files?.[0];
        if (dropped) onPick(dropped);
    };

    const hiddenInput = (
        <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            onChange={(event) => takeFirstPdf(event.target.files)}
            disabled={disabled}
            className="hidden"
        />
    );

    if (file) {
        return (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-300 bg-white">
                <DocumentTextIcon className="w-5 h-5 flex-shrink-0 text-gray-400" />
                <div className="flex-1 min-w-0 flex flex-col">
                    <span className="text-sm font-semibold text-gray-800 truncate">
                        {file.name}
                    </span>
                    <span className="text-xs text-gray-400">{formatBytes(file.size)}</span>
                </div>
                <button
                    type="button"
                    onClick={() => onPick(null)}
                    disabled={disabled}
                    aria-label="Remove the selected PDF"
                    className="flex-shrink-0 text-gray-400 hover:text-gray-800 transition-colors disabled:opacity-50"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>
            </div>
        );
    }

    if (storedFileId) {
        return (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-gray-300 bg-white">
                <DocumentTextIcon className="w-5 h-5 flex-shrink-0 text-gray-400" />
                <div className="flex-1 min-w-0 flex flex-col">
                    <span className="text-sm font-semibold text-gray-800 truncate">
                        {storedName ?? 'Current PDF'}
                    </span>
                    <span className="text-xs text-gray-400">Stored with this document</span>
                </div>
                <button
                    type="button"
                    onClick={onRemoveStored}
                    disabled={disabled}
                    aria-label="Remove the current PDF"
                    title="Remove — a replacement will be required"
                    className="flex-shrink-0 text-gray-400 hover:text-gray-800 transition-colors disabled:opacity-50"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>
            </div>
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={disabled}
                onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    takeFirstPdf(event.dataTransfer.files);
                }}
                className={`flex flex-col items-center justify-center gap-1.5 px-4 py-6 w-full rounded-lg border-2 border-dashed transition-colors disabled:opacity-50 ${
                    isDragging
                        ? 'border-gray-400 bg-gray-100'
                        : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
                }`}
            >
                <ArrowUpTrayIcon className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-semibold text-gray-700">Choose a PDF</span>
                <span className="text-xs text-gray-400">or drop it here</span>
            </button>
            {hiddenInput}
        </>
    );
};

export default PdfField;
